import React from 'react'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import CloseIcon from '@/assets/icons/drawer-close-icon'
import {Tag} from '@/components/ui/tag'
import {useCioFacetSelector, SET_CIO_SELECTED_FACETS} from '@/context/cio-facet'
import {isEmptyObject} from '@/utils/common'

interface FacetOption {
    value: string
    display_name: string
}

interface SelectedFacetFilters {
    [category: string]: FacetOption[]
}

interface FilterTagsProps {
    'data-testid'?: string
}

export const FilterTags: React.FC<FilterTagsProps> = ({'data-testid': dataTestId}) => {
    const selectedFacetFilters = useCioFacetSelector(
        (context) => context?.state.selectedCioFacets
    ) as SelectedFacetFilters
    const dispatchFacetFilters = useCioFacetSelector((context) => context?.dispatch)

    const handleSelectedFilter = (category: string, value: string) => {
        const newSelectedFilters = {...selectedFacetFilters}
        newSelectedFilters[category] = newSelectedFilters[category].filter(
            (item) => item.value !== value
        )
        if (newSelectedFilters[category].length === 0) {
            delete newSelectedFilters[category]
        }
        dispatchFacetFilters({
            type: SET_CIO_SELECTED_FACETS,
            payload: newSelectedFilters
        })
    }

    return (
        <div className="flex flex-wrap mt-4 gap-4">
            {Object.entries(selectedFacetFilters).map(([category, values]) =>
                values.map((item) => (
                    <div
                        key={`${category}-${item.value}`}
                        className="flex items-center text-sm rounded-lg py-1 bg-gray-01 text-primary"
                    >
                        <Tag
                            title={
                                item.display_name.startsWith('-inf')
                                    ? item.display_name.replace('-inf -', '').trim() + ' and less'
                                    : item.display_name.startsWith('0')
                                    ? item.display_name.replace('0 -', '').trim() + ' and less'
                                    : item.display_name.endsWith('inf')
                                    ? item.display_name.replace('- inf', '').trim() + ' and more'
                                    : item.display_name.endsWith('0')
                                    ? item.display_name.replace('- 0', '').trim() + ' and more'
                                    : item.display_name
                            }
                            data-testid={dataTestId || 'filterTags'}
                        />
                        <button
                            onClick={() => handleSelectedFilter(category, item.value)}
                            className="ml-1 focus:outline-none"
                        >
                            <VisuallyHidden.Root>
                                <span>Remove {item.display_name}</span>
                            </VisuallyHidden.Root>
                            <CloseIcon
                                className="h-[.687rem] mr-1 fill-accent"
                                data-testid={dataTestId || 'filterTags-CloseIcon'}
                            />
                        </button>
                    </div>
                ))
            )}
            {/* Clear All Button (Only show if there are tags) */}
            {!isEmptyObject(selectedFacetFilters) && (
                <button
                    onClick={() =>
                        dispatchFacetFilters({
                            type: SET_CIO_SELECTED_FACETS,
                            payload: {}
                        })
                    }
                    className="text-primary text-sm text-grotesque-medium font-bold underline"
                >
                    Clear all
                </button>
            )}
        </div>
    )
}
